/* eslint-disable react/no-unstable-nested-components */
import { Collapse } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  AutoEventsIcon as autoEventsIcon,
  EcgDisclosureIcon as ecgDisclosureIcon,
  IconArrowCollapse as arrowCollapse,
  IconArrowExpand as arrowExpand,
  UnappliedChangesIcon as unappliedChangesIcon,
} from '@/shared/ui';
import { AUTO_EVENTS, ECG_DISCLOSURE } from '@/shared/config';
import { AUTO_EVENT_TABS, DEVICE_EVENT_TABS } from '../constants';
import useStripLeftColumn from './useStripLeftColumn';
import '../styles.scss';

const { Panel } = Collapse;

type TabTagProps = {
  tab: any;
  icon?: any;
  onClickTab: (tab: any) => void;
  activeButton?: any;
  eventCount?: any;
  title?: any;
  isUnsavedChanges?: any;
  shouldDisable?: any;
  alwaysShow?: any;
};

const TabTag = ({
  tab,
  icon,
  onClickTab,
  activeButton,
  eventCount,
  title,
  isUnsavedChanges,
  shouldDisable,
  alwaysShow,
}: TabTagProps) => (
  <button
    id={`strips-tab-${tab}`}
    className={classnames('templates-btn', activeButton === tab ? '--active' : '')}
    onClick={() => {
      onClickTab(tab);
    }}
    disabled={shouldDisable && !eventCount}
  >
    {icon && <img className="tab-icon" src={icon} alt="Hr summary icon" />}
    {title}
    {isUnsavedChanges && (
      <img
        src={unappliedChangesIcon}
        alt="Unapplied changes icon"
        className="unapplied-changes-indicator"
      />
    )}
    {(!!eventCount || alwaysShow) && (
      <div className="ml-auto" id={`eventCountStripEvents${tab}`}>
        <div className="event-count-column">{eventCount > 99 ? '99+' : eventCount || 0}</div>
        {eventCount > 99 && (
          <UncontrolledTooltip
            className="custom-tooltip"
            target={`eventCountStripEvents${tab}`}
            placement="bottom-end"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            {eventCount}
          </UncontrolledTooltip>
        )}
      </div>
    )}
  </button>
);

/**
 * Renders the strips management sidebar listing the device event tabs, the auto event tabs and the ECG disclosure entry. Reads the active tab, per-tab counts and unsaved-change flags from useStripLeftColumn, puts the device tabs inside a collapsible panel open by default, and separates each group with a divider.
 *
 * @returns The left column with its collapsible device panel, auto event tabs and disclosure tab.
 */
const StripLeftColumn = () => {
  const { activeButton, emptySelectedKey, onClickTab, getCount, isUnsavedChange } =
    useStripLeftColumn();

  /**
   * Renders the auto events section of the strips management sidebar. Draws the top-level Auto events tab with its event count and unsaved-change marker, and expands the per-template AUTO_EVENT_TABS below it unless the auto-events group is empty.
   *
   * @returns The auto events tab block with its nested template tabs.
   */
  const renderAutoEventTabs = () => (
    <div>
      <div>
        <TabTag
          tab={AUTO_EVENTS}
          icon={autoEventsIcon}
          onClickTab={onClickTab}
          activeButton={activeButton}
          eventCount={getCount(AUTO_EVENTS)}
          title="Auto events"
          isUnsavedChanges={isUnsavedChange(AUTO_EVENTS)}
          alwaysShow
        />
      </div>
      {!emptySelectedKey.isEmptyAutoEvents && (
        <div className="strip-templates-btn-wrapper">
          {_.map(AUTO_EVENT_TABS, (tab) => (
            <TabTag
              tab={tab.key}
              title={tab.title}
              onClickTab={onClickTab}
              activeButton={activeButton}
              eventCount={getCount(tab.key)}
              shouldDisable
              isUnsavedChanges={isUnsavedChange(tab.key)}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div id="left-column" className="left-column strips-left-column">
      <div className="column-content">
        <Collapse
          defaultActiveKey={['1']}
          expandIconPosition="start"
          expandIcon={({ isActive }) => (
            <img src={isActive ? arrowExpand : arrowCollapse} alt="arrow icon" />
          )}
        >
          <Panel header="Device events" key="1" className="panel-header --device-events">
            {_.map(DEVICE_EVENT_TABS, (tab) => (
              <TabTag
                tab={tab.key}
                title={tab.title}
                onClickTab={onClickTab}
                activeButton={activeButton}
                eventCount={getCount(tab.key)}
                isUnsavedChanges={isUnsavedChange(tab.key)}
                alwaysShow
              />
            ))}
          </Panel>
        </Collapse>
        <div className="left-column-divider" />
        {renderAutoEventTabs()}
        <div className="left-column-divider" />
        <TabTag
          tab={ECG_DISCLOSURE}
          title="ECG disclosure"
          onClickTab={onClickTab}
          activeButton={activeButton}
          icon={ecgDisclosureIcon}
        />
      </div>
    </div>
  );
};

export default StripLeftColumn;
